import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import * as outbox from "../core/outbox.js";
import { activeSession, refreshActiveSession, profile, muscleName, cacheActiveSession, markFinishing } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { spinner, toast, confirmDialog, openModal, errorState } from "../components/ui.js";
import { exerciseThumb, supersetLabels, setSummaryText } from "../components/common.js";
import { onUnmount } from "../core/lifecycle.js";
import { openExercisePicker } from "../components/ExercisePicker.js";
import { openExerciseReplacement } from "../components/ExerciseReplacement.js";
import { openPlateCalculator } from "../components/PlateCalculator.js";
import { startRest, syncRest, stopRest, attachRest } from "../components/RestTimer.js";
import { toDisplayWeight, toStorageKg, weightLabel, fmtDuration, fmtVolume, fmtWeight, parseDate,
         toDisplayDistance, toStorageDistanceM, distanceUnit, parseTimeInput, fmtTimeShort, relativeDay, epley1RM } from "../lib/format.js";
import * as health from "../lib/integrations/health.js";
import { maxHeartRate, ageFromBirthYear } from "../lib/health.js";
import { isInMorningWeightWindow } from "../lib/bodyweight.js";
import { editNextSession } from "../components/trainingPlan.js";
import { comparisonText } from "../components/weeklyReview.js";
import { bowLoose } from "../components/bowProgress.js";
import { restHrEnabled, recoveryThreshold, recoveryState, PEAK_WINDOW_MS } from "../lib/restHr.js";
import * as nativeLocal from "../lib/nativeLocal.js";
import * as volumeKeys from "../lib/nativeVolumeKeys.js";

// Which set columns an exercise's metric type uses, in render order.
const METRIC = {
  weight_reps: { cols: ["weight", "reps", "rpe"] },
  bodyweight_reps: { cols: ["reps", "rpe"] },
  duration: { cols: ["time", "rpe"] },
  distance_duration: { cols: ["distance", "time"] },
};
const metricOf = (ex) => METRIC[ex.metric_type] ? ex.metric_type : "weight_reps";
// Active set columns for a metric — drops RPE when the user opted out (profile hide_rpe).
// Purely visual: existing RPE values stay stored, and unlogged RPE already counts as a hard set.
const rpeHidden = () => !!profile()?.hide_rpe;
const colsFor = (mt) => (rpeHidden() ? METRIC[mt].cols.filter((c) => c !== "rpe") : METRIC[mt].cols);
const EXEC_HINT_KEY = "wt_exec_hint_off"; // sticky dismissal of the "how to run this workout" card
const FOCUS_KEY = "wt_focus_mode"; // logger focus mode (collapse done exercises); default ON, "0" = off
const focusMode = () => { try { return localStorage.getItem(FOCUS_KEY) !== "0"; } catch { return true; } };
const COL_HEAD = { weight: () => weightLabel(), reps: () => "Reps", rpe: () => "RPE", time: () => "Time", distance: () => distanceUnit() };

export default function Session() {
  const root = h("div", { class: "session-screen" });
  let session = null;
  let timerInterval = null;
  let remoteSyncInterval = null;
  let remoteSyncing = false;
  let replacementModal = null;
  let destroyed = false;
  let unsubSync = null;
  let stopHr = null;   // teardown fn for the native live-HR foreground service (Android only)
  let lastBpm = null;  // last live BPM, kept so body re-renders don't blank the readout
  // Recent live samples [{bpm, at}], trimmed to the peak window. The rest-recovery suggestion
  // needs the shape of the last few minutes, not just the latest number: "back down" is only
  // meaningful relative to what the set actually pushed the heart rate up to.
  let hrLog = [];
  // Training together: the live pairing (or null) plus who else is training right now. Polled on
  // its own slow timer — presence changes on the scale of someone walking into a gym, not of a
  // set being logged, and the two-second logger poll has no business carrying it.
  let pairState = { pair: null, candidates: [] };
  let pairInterval = null;
  let pairBusy = false;
  let stopVolumeKeys = null; // teardown for the hardware volume-key capture (Android only)
  // Focus mode: exercises the user re-expanded by tap, overriding the "done → collapsed"
  // rule until they collapse them again (or finish the last set anew).
  const expandedOverride = new Set();
  const suggestionUndo = new Map();

  // A value is only safe to hand the renderer if it's an actual session object. Anything else —
  // a truncated snapshot, a proxy interstitial that slipped past api.js — must be re-pulled from
  // the server rather than rendered (that's what produced "Couldn't load your session").
  const isLoaded = (s) => !!s && typeof s === "object" && Array.isArray(s.exercises);

  init();

  async function init({ refetch = false } = {}) {
    setChildren(root, h("div", { class: "screen" }, spinner("Loading your session…")));
    // Wrap the whole load+render: a failed fetch or a throw mid-render must never leave the user
    // on an unrecoverable "Loading your session…" spinner — show a retry instead. (The fetch has
    // its own 15s timeout in api.js; this is the backstop for everything after it.)
    try {
      session = refetch ? null : activeSession();
      if (!isLoaded(session)) session = await refreshActiveSession();
      if (!isLoaded(session)) {
        if (session) activeSession(null); // never leave a non-session parked in the store
        navigate("/start", { replace: true });
        return;
      }
      cacheActiveSession(session);
      if (unsubSync) unsubSync(); // a retry must not stack outbox subscriptions
      unsubSync = outbox.onChange(updateSync);
      renderAll();
    } catch (e) {
      // First failure: the in-memory session may simply be stale. Re-pull from the server and
      // render again before bothering the user — that alone clears every transient case, so the
      // error screen is reserved for something genuinely wrong (and now says what).
      if (!refetch) return init({ refetch: true });
      console.error("Session load failed", e);
      setChildren(root, h("div", { class: "screen" },
        errorState(e?.message || "Couldn't load your session.", () => init({ refetch: true }))));
    }
  }

  function destroy() {
    destroyed = true;
    replacementModal?.close();
    attachRest();
    if (timerInterval) clearInterval(timerInterval);
    if (remoteSyncInterval) clearInterval(remoteSyncInterval);
    remoteSyncInterval = null;
    if (pairInterval) clearInterval(pairInterval);
    pairInterval = null;
    document.removeEventListener("visibilitychange", onRemoteVisibility);
    if (unsubSync) { unsubSync(); unsubSync = null; }
    if (stopHr) { const s = stopHr; stopHr = null; s(); } // stop the live-HR service
    // Hand the volume button back the moment the logger goes away — it must never be swallowed
    // outside the one screen that asked for it.
    if (stopVolumeKeys) { const s = stopVolumeKeys; stopVolumeKeys = null; s(); }
    // NOT stopRest(): the rest timer is a body-level overlay for the workout, not for this
    // screen. Opening an exercise page mid-rest (or glancing at the dashboard) used to kill the
    // countdown. It's stopped explicitly when the workout itself ends — finish()/discard().
  }

  // Persist local state so a reload (incl. offline) restores the workout, and keep
  // the shared signal in sync for the dashboard "resume" card.
  function touch() {
    activeSession(session);
    cacheActiveSession(session);
    updateProgress();
  }

  // The exercise whose set started the current rest, resolved through the shared
  // `rest_source_set_id` rather than a local variable — so the *other* phone, which never logged
  // that set, still knows where in the workout the rest came from and can name what's next.
  function restSourceExercise() {
    const id = session?.rest_source_set_id;
    if (id == null) return null;
    return session.exercises.find((e) => e.sets.some((s) => String(s.id) === String(id))) || null;
  }

  const restOptions = () => ({
    container: () => !destroyed && root.querySelector("#session-rest-slot"),
    // Whose set is on the clock. Null for your own — the countdown you started needs no caption;
    // it's the one somebody else started that you can't otherwise account for.
    startedBy: () => {
      const from = session?.rest_from_user_id;
      if (from == null || String(from) === String(profile()?.id)) return null;
      return pairState.pair?.partner?.id === from ? pairState.pair.partner.username : "Your partner";
    },
    // A suggestion only, re-read as samples arrive: whether the band says you're back down. The
    // countdown never consults it — a reading can be most of a minute stale, and a timer that
    // wouldn't finish because of a stale number would strand the user for reasons they can't see.
    recovery: () => recoveryState(hrLog, { threshold: recoveryThreshold(ageFromBirthYear(profile()?.birth_year)) }),
    // Read lazily by the always-on rest face, every poll — so a set logged on the other device
    // moves this on too. Null (rest with no known source, everything finished) leaves the face
    // exactly as it was before this shipped.
    nextSet: () => {
      const n = nextPendingSet(restSourceExercise());
      if (!n) return null;
      const target = setSummaryText(metricOf(n.ex), n.set);
      return {
        exerciseName: n.ex.exercise_name,
        // setSummaryText formats sets that have already been *logged*. A set you haven't reached
        // yet can be half-filled — no weight typed gives "kg×5", an untouched one "—kg×—" — and
        // a hole read from across the bench is worse than no target at all. Every complete form
        // (60kg×5, 5 reps, 1:30, 5.0km · 30:00) starts with its number; anything else is a gap.
        target: /^[0-9]/.test(target) ? target : null,
        index: n.index,
        total: n.total,
      };
    },
    onDeadlineChange: (endsAt) => {
      session.rest_ends_at = endsAt;
      touch();
      api.updateSessionRest(session.id, endsAt);
    },
    onSkip: () => {
      session.rest_ends_at = null;
      session.rest_source_set_id = null;
      touch();
      api.updateSessionRest(session.id, null);
    },
  });

  function syncSharedRest() {
    const endMs = Date.parse(session?.rest_ends_at || "");
    if (Number.isFinite(endMs) && endMs > Date.now()) syncRest(session.rest_ends_at, restOptions());
    else stopRest();
  }

  // The logger is deliberately poll-based rather than a permanent socket: one tiny read every
  // two seconds only while this screen is visible, and the response is empty unless sync_version
  // changed. Android receives the rest deadline separately through data-only FCM, so a locked
  // phone can arm its OS alarm without keeping this WebView or poll alive.
  function startRemoteSync() {
    if (remoteSyncInterval) return;
    document.addEventListener("visibilitychange", onRemoteVisibility);
    remoteSyncInterval = setInterval(syncRemoteSession, 2000);
    void syncRemoteSession();
  }

  async function onRemoteVisibility() {
    if (document.visibilityState !== "visible") return;
    // Order matters: a "+30 s" tapped on the lock screen has to reach the server BEFORE the poll
    // reads the old deadline back, or the tap is silently undone the moment you unlock the phone.
    await reconcileNativeRest();
    void syncRemoteSession();
  }

  // A rest change made from a notification button while this page wasn't running. The change is
  // already live on the phone's alarm; the server (which owns the deadline) is the one that needs
  // telling. No-op everywhere except an Android shell that actually has something pending.
  async function reconcileNativeRest() {
    if (destroyed || !session) return;
    const change = await nativeLocal.takePendingRestChange();
    if (!change || destroyed || !session) return;
    session.rest_ends_at = change.endsAt;
    if (!change.endsAt) session.rest_source_set_id = null;
    touch();
    try {
      await api.updateSessionRest(session.id, change.endsAt);
    } catch {
      // Offline: the alarm on this phone is still correct, and the next foreground tries again
      // only if the user taps again — acceptable for a thirty-second nudge.
    }
    syncSharedRest();
  }

  async function syncRemoteSession() {
    if (destroyed || remoteSyncing || replacementModal || document.visibilityState !== "visible" || !session) return;
    // Never replace values underneath the user's fingers or before this device's optimistic
    // outbox writes have reached the server.
    if (outbox.pendingCount() || root.querySelector("input:focus, textarea:focus, select:focus")) return;
    remoteSyncing = true;
    const requestedSession = session;
    try {
      const data = await api.activeSessionSync(session.sync_version || 0);
      if (!data || requestedSession !== session || String(data.session_id) !== String(session.id) || destroyed || replacementModal || outbox.pendingCount()
        || root.querySelector("input:focus, textarea:focus, select:focus")) return;
      if (!data.changed) {
        syncSharedRest();
        return;
      }

      // A replacement can change an exercise's identity or split off its remaining sets.
      // Fetch the full shape when the lightweight poll reports a changed exercise list.
      if (data.exercises && (data.exercises.length !== session.exercises.length || data.exercises.some((ex, i) =>
        String(ex.id) !== String(session.exercises[i]?.id) || ex.library_exercise_id !== session.exercises[i]?.library_exercise_id))) {
        const latest = await api.activeSession();
        if (destroyed || requestedSession !== session || replacementModal || outbox.pendingCount() || root.querySelector("input:focus, textarea:focus, select:focus")) return;
        if (!isLoaded(latest) || latest.id !== session.id) return;
        session = latest;
        suggestionUndo.clear();
        touch();
        renderBody(root.querySelector("#session-body"));
        syncSharedRest();
        return;
      }

      const remoteByExercise = new Map();
      for (const set of data.sets || []) {
        const key = String(set.session_exercise_id);
        if (!remoteByExercise.has(key)) remoteByExercise.set(key, []);
        remoteByExercise.get(key).push(set);
      }
      for (const ex of session.exercises) {
        const remote = remoteByExercise.get(String(ex.id));
        if (!remote) continue;
        const local = new Map(ex.sets.map((s) => [String(s.id), s]));
        ex.sets = remote.map((row) => Object.assign(local.get(String(row.id)) || {}, row));
      }
      session.sync_version = data.sync_version;
      session.rest_ends_at = data.rest_ends_at || null;
      session.rest_source_set_id = data.rest_source_set_id ?? null;
      session.rest_from_user_id = data.rest_from_user_id ?? null;
      touch();
      // A sync_version bump is also how the server tells us a pairing was offered, accepted or
      // dropped — pick it up now rather than waiting out the slow pairing poll.
      void refreshPair();
      const body = root.querySelector("#session-body");
      if (body) renderBody(body);
      syncSharedRest();
      updateSync();
    } catch {
      // A transient poll failure must never disturb live logging; the next tick catches up.
    } finally {
      remoteSyncing = false;
    }
  }

  // Live sync/offline chip in the session header (driven by the outbox).
  function updateSync() {
    const el = root.querySelector("#sync-status");
    if (!el) return;
    const offline = outbox.isOffline();
    const n = outbox.pendingCount();
    let text = "";
    if (offline) text = n ? `Offline · ${n} to sync` : "Offline";
    else if (n > 0) text = outbox.isFlushing() ? `Syncing ${n}…` : `${n} to sync`;
    else if (outbox.hasError()) text = "Sync issue";
    el.style.cssText =
      "font-size:.7rem;font-weight:600;padding:3px 9px;border-radius:999px;white-space:nowrap;" +
      (text ? "" : "display:none;") +
      (offline
        ? "background:#92400e;color:#fff;"
        : "background:transparent;color:var(--muted);border:1px solid var(--border);");
    setChildren(el, text || null);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  function renderAll() {
    const head = sessionHead();
    const list = h("div", { class: "screen", id: "session-body" });
    renderBody(list);
    setChildren(root, head, list, sessionDock());
    updateProgress();
    startTimer(head.querySelector("#s-timer"));
    updateSync();
    ensureLiveHr();
    ensureVolumeKeys();
    void reconcileNativeRest(); // a lock-screen "+30 s" from before this page loaded
    syncSharedRest();
    startRemoteSync();
    startPairPoll();
  }

  function sessionDock() {
    return h("div",{class:"session-dock","aria-label":"Workout actions"},
      h("div",{id:"session-rest-slot"}),
      h("div",{class:"session-dock-row"},
        h("button",{class:"btn btn-ghost session-nav",onClick:()=>{
          const content=h("div",{class:"session-menu"});
          const modal=openModal({title:"Workout navigation",content});
          for(const [label,path] of [["Home","/"],["Workout plan","/workouts"],["Progress","/history"],["Profile","/profile"]])content.append(h("button",{class:"btn btn-ghost btn-block",onClick:()=>{modal.close();navigate(path);}},label));
        }},icon("list",{size:20}),"Navigate"),
        h("button",{id:"session-next",class:"btn btn-primary session-next",onClick:()=>{
          const n=nextPendingSet(restSourceExercise());
          if(!n){root.querySelector(".session-finish")?.focus();return;}
          const row=root.querySelector(`.set-row[data-set="${n.set.id}"]`);
          row?.scrollIntoView({block:"center",behavior:matchMedia("(prefers-reduced-motion: reduce)").matches?"instant":"smooth"});
          row?.querySelector("input")?.focus({preventScroll:true});
        }},"Go to next set")));
  }

  // ── Hardware volume key ───────────────────────────────────────────────────────
  // Opt-in, and only while the logger is on screen — the capture is released on unmount and
  // expires natively if this page stops running, so the button is never left hijacked.
  async function ensureVolumeKeys() {
    if (stopVolumeKeys || !volumeKeys.available() || !volumeKeys.volumeKeyEnabled()) return;
    stopVolumeKeys = await volumeKeys.captureVolumeKeys(onVolumeKey);
  }

  // Completes the set the logger would scroll you to anyway — the same nextPendingSet() the
  // always-on rest face names. Done by clicking the row's own ✓ rather than calling completeSet()
  // directly, so a hardware press takes exactly the same path as a tap, including capturing
  // whatever is currently typed in that row's inputs.
  function onVolumeKey() {
    if (replacementModal) return;
    const n = nextPendingSet(restSourceExercise());
    if (!n) {
      toast("No sets left to complete", "warn");
      return;
    }
    const btn = root.querySelector(`.set-row[data-set="${n.set.id}"] .set-check`);
    if (btn) btn.click();
  }

  function startTimer(el) {
    if (timerInterval) clearInterval(timerInterval);
    const start = parseDate(session.started_at)?.getTime() ?? Date.now();
    const update = () => (el.textContent = fmtDuration(Math.floor((Date.now() - start) / 1000)));
    update();
    timerInterval = setInterval(update, 1000);
  }

  function sessionHead() {
    return h(
      "header",
      { class: "session-head" },
      h("div", { class: "session-toolbar" },
        h("div", { class: "session-clock" }, h("span", { class: "session-live" }, "IN SESSION"),
          h("div", { class: "s-timer", id: "s-timer" }, "0:00")),
        h("div", { class: "session-actions" },
          focusToggle(),
          h("button", { class: "btn btn-success session-finish", onClick: finish }, icon("check", { size: 18 }), "Finish"))),
      h("h1", { class: "s-name", tabindex:"-1" }, session.name || "Freestyle"),
      h("div", { class: "session-progress-meta" },
        h("span", { id: "session-progress-label" }),
        h("span", { id: "sync-status", style: { display: "none" } })),
      h("div", { class: "session-progress-track", role: "progressbar", "aria-label": "Working sets completed", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": 0 },
        h("div", { class: "session-progress-fill" }))
    );
  }

  // Update only summary text and attributes: never replace focused set inputs.
  function updateProgress() {
    const nextButton=root.querySelector("#session-next");
    if(nextButton){
      const n=nextPendingSet(restSourceExercise());
      nextButton.textContent=n ? `Go to next set · ${n.ex.exercise_name}` : "All sets logged · review Finish";
    }
    const label = root.querySelector("#session-progress-label");
    if (!label || !session) return;
    const sets = session.exercises.flatMap((ex) => ex.sets).filter((set) => !set.is_warmup);
    const done = sets.filter((set) => set.is_completed).length;
    const percent = sets.length ? Math.round(done / sets.length * 100) : 0;
    label.textContent = `${done} / ${sets.length} working sets · ${session.exercises.length} exercises`;
    const track = root.querySelector(".session-progress-track");
    track.setAttribute("aria-valuenow", String(percent));
    track.setAttribute("aria-valuetext", `${done} of ${sets.length} working sets completed`);
    track.firstElementChild.style.width = `${percent}%`;
  }

  // Focus-mode toggle: collapse finished exercises to summary rows so the current one is
  // the spotlight. Presentation-only and per-device (localStorage), like the RPE column.
  function focusToggle() {
    const btn = h("button", {
      class: "icon-btn ghost", id: "focus-toggle",
      title: "Focus mode — collapse finished exercises",
      "aria-label": "Toggle focus mode",
      "aria-pressed": String(focusMode()),
      style: { color: focusMode() ? "var(--accent)" : "var(--text-3)" },
      onClick: () => {
        const on = !focusMode();
        try { localStorage.setItem(FOCUS_KEY, on ? "1" : "0"); } catch {}
        btn.style.color = on ? "var(--accent)" : "var(--text-3)";
        btn.setAttribute("aria-pressed", String(on));
        renderBody(root.querySelector("#session-body"));
        toast(on ? "Focus mode on — finished exercises collapse" : "Focus mode off", "success");
      },
    }, icon("target", { size: 20 }));
    return btn;
  }

  function staleSessionBanner() {
    const startMs = parseDate(session?.started_at)?.getTime() ?? Date.now();
    const elapsedHours = (Date.now() - startMs) / 3600000;
    if (elapsedHours < 4) return null;
    return h("div", {
      class: "card",
      style: { background: "var(--surface-2)", borderLeft: "4px solid var(--warn)", padding: "10px 14px", fontSize: ".82rem", marginBottom: "12px" }
    },
      h("strong", null, "Resumed workout: "),
      "Started over 4 hours ago. On finish, duration will be calculated from your completed sets to keep training history accurate."
    );
  }

  function renderBody(list) {
    setChildren(list,
      staleSessionBanner(),
      pairCard(),
      liveHrCard(),
      executionHint(),
      ...session.exercises.map(exerciseCard),
      h("button", { class: "btn btn-ghost btn-block mt", onClick: addExercise }, icon("plus", { size: 20 }), "Add exercise"),
      session.exercises.length ? sessionNotesCard() : null,
      session.exercises.length
        ? h("button", { class: "btn btn-danger btn-block mt-lg", style: { background: "transparent", color: "var(--danger)" }, onClick: discard }, icon("trash", { size: 18 }), "Discard workout")
        : h("p", { class: "dim center mt-lg" }, "Add an exercise to get started.")
    );
  }

  // How-to-run-this-workout hint, tailored to the user's training goal. Strength/hypertrophy/
  // general → run straight sets with full rest (the user's specific ask: don't circuit one set of
  // each); endurance-only → short rests / circuits are fine. Sticky-dismissible so it never nags.
  function executionHint() {
    try { if (localStorage.getItem(EXEC_HINT_KEY) === "1") return null; } catch {}
    const goals = [profile()?.training_goal, profile()?.training_goal_secondary].filter(Boolean);
    const enduranceOnly = goals.length > 0 && goals.every((g) => g === "endurance");
    const rest = goals.includes("strength") ? "3–5 min on heavy lifts" : "about 1–3 min";
    const driver = goals.includes("strength") ? "strength" : goals.includes("hypertrophy") ? "muscle growth" : "your results";
    const body = enduranceOnly
      ? "Endurance focus: keep reps higher and rests short (30–90s). Circuiting one set of each exercise in a loop works well here for conditioning."
      : `Do straight sets — finish all the sets of one exercise, resting ${rest} between each, before moving to the next. Avoid circuiting one set of each: full rest keeps your load high, which is what drives ${driver}.`;
    const card = h("div", { class: "session-guidance" },
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "flex-start" } },
        h("div", { style: { color: "var(--accent)", marginTop: "1px" } }, icon("info", { size: 18 })),
        h("details", { class: "grow" },
          h("summary", null, "How to run this workout"),
          h("p", { class: "dim" }, body)),
        h("button", {
          class: "icon-btn ghost", "aria-label": "Dismiss",
          style: { flex: "0 0 auto", fontSize: "1.1rem", lineHeight: 1 },
          onClick: () => { try { localStorage.setItem(EXEC_HINT_KEY, "1"); } catch {} card.remove(); },
        }, "×")
      )
    );
    return card;
  }

  // ── Training together ─────────────────────────────────────────────────────────
  // Two people alternating on one bench share a tempo: a set finished by either starts the same
  // rest on both phones. The pairing is explicit on both sides and either can drop it — see
  // server/services/pairing.ts for why it has to be, since this crosses accounts.
  function startPairPoll() {
    if (pairInterval) return;
    void refreshPair();
    // Presence changes on the scale of someone arriving at the gym. Fifteen seconds is live
    // enough for "they're training now" and nowhere near often enough to matter.
    pairInterval = setInterval(() => {
      if (document.visibilityState === "visible") void refreshPair();
    }, 15000);
  }

  async function refreshPair() {
    if (destroyed || pairBusy) return;
    pairBusy = true;
    try {
      const next = await api.restPair();
      if (destroyed || !next) return;
      // Only touch the DOM when something actually changed: this card sits above the exercise
      // list, and rebuilding it under a scrolling thumb every fifteen seconds is its own bug.
      if (JSON.stringify(next) === JSON.stringify(pairState)) return;
      pairState = next;
      const card = root.querySelector("#pair-card");
      const rebuilt = pairCard();
      if (card && rebuilt) card.replaceWith(rebuilt);
      else if (card || rebuilt) renderBody(root.querySelector("#session-body"));
    } catch {
      // Offline or a transient failure: keep whatever we last knew rather than blanking the card.
    } finally {
      pairBusy = false;
    }
  }

  async function pairAction(fn, failure) {
    try {
      await fn();
      await refreshPair();
    } catch (e) {
      toast(e?.status === 0 ? "You're offline — reconnect to do that." : e?.message || failure, "error");
    }
  }

  function pairCard() {
    const { pair, candidates } = pairState;
    const body = pair ? pairedBody(pair) : inviteBody(candidates);
    if (!body) return null;
    return h("div", { class: "card", id: "pair-card", style: { marginBottom: "12px" } }, body);
  }

  function pairedBody(pair) {
    const who = pair.partner?.username || "your partner";
    if (pair.status === "accepted") {
      return h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
        h("div", { style: { color: "var(--accent)" } }, icon("users", { size: 20 })),
        h("div", { class: "grow" },
          h("div", { style: { fontWeight: 700 } }, `Training with ${who}`),
          h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } },
            "Either of you finishing a set starts the same rest on both phones.")),
        h("button", { class: "btn btn-ghost", onClick: () => pairAction(api.endPair, "Couldn't stop") }, "Stop"));
    }
    // Pending: the invitee decides, the inviter waits. Nobody's rest changes until then.
    if (pair.role === "invitee") {
      return h("div", null,
        h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
          h("div", { style: { color: "var(--accent)" } }, icon("users", { size: 20 })),
          h("div", { class: "grow" },
            h("div", { style: { fontWeight: 700 } }, `${who} wants to share rest timers`),
            h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } },
              "You'll both see the same countdown, and either of you can stop it.")) ),
        h("div", { class: "btn-row mt" },
          h("button", { class: "btn btn-primary grow", onClick: () => pairAction(api.acceptPair, "Couldn't accept") }, "Train together"),
          h("button", { class: "btn btn-ghost", onClick: () => pairAction(api.endPair, "Couldn't decline") }, "No thanks")));
    }
    return h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
      h("div", { style: { color: "var(--text-3)" } }, icon("users", { size: 20 })),
      h("div", { class: "grow" },
        h("div", { style: { fontWeight: 600 } }, `Waiting for ${who}…`),
        h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, "Nothing changes until they accept.")),
      h("button", { class: "btn btn-ghost", onClick: () => pairAction(api.endPair, "Couldn't cancel") }, "Cancel"));
  }

  // Nobody training who you could pair with → no card at all. An empty "nobody's here" panel
  // above your sets every workout would be a permanent reminder of a feature you can't use.
  function inviteBody(candidates) {
    if (!candidates?.length) return null;
    return h("div", null,
      h("div", { class: "dim", style: { fontSize: ".8rem", fontWeight: 600, marginBottom: "6px" } }, "Training now"),
      ...candidates.map((c) =>
        h("div", { class: "row-between", style: { padding: "4px 0" } },
          h("div", null,
            h("div", { style: { fontWeight: 600 } }, c.username),
            h("div", { class: "dim", style: { fontSize: ".78rem" } }, c.session_name || "Workout")),
          // "Invite", not "Train together": tapping this doesn't start anything, it asks. The
          // button that does start it is the one they tap.
          h("button", { class: "btn btn-ghost", onClick: () => pairAction(() => api.invitePair(c.user_id), "Couldn't invite") },
            icon("users", { size: 16 }), "Invite"))));
  }

  // ── Live heart rate (Android Health Connect; cardio sessions only) ────────────
  // Only the sideloaded Android shell exposes the native plugin; on plain web health.available()
  // is false so none of this renders or runs. Mi Fitness writes HR into Health Connect at ~1-min
  // granularity, so the readout refreshes roughly once a minute — honest, not per-second.
  const hasCardio = () => !!session?.exercises?.some((e) => e.metric_type === "distance_duration");
  // Lifting sessions run the HR feed only on an explicit opt-in: it means a foreground service and
  // an ongoing notification for the whole workout, which is not something to switch on for
  // everyone so a rest can offer one extra line of advice.
  const wantsLiveHr = () => hasCardio() || restHrEnabled();

  // Start the native foreground HR service once it's wanted. Idempotent (a second call while
  // already running is a no-op). Torn down in destroy() on finish/discard/unmount.
  async function ensureLiveHr() {
    if (stopHr || !health.available() || !wantsLiveHr()) return;
    try {
      stopHr = await health.startLiveHr((bpm) => updateLiveHr(bpm));
    } catch {
      stopHr = null;
    }
  }

  function updateLiveHr(bpm) {
    lastBpm = bpm;
    const now = Date.now();
    hrLog.push({ bpm, at: now });
    hrLog = hrLog.filter((s) => now - s.at <= PEAK_WINDOW_MS);
    const card = root.querySelector("#live-hr");
    if (card) setChildren(card, ...liveHrContent(bpm));
  }

  // 5-zone model off the Tanaka HR-max estimate (needs birth_year). null when age is unknown.
  function hrZone(bpm) {
    const hrMax = maxHeartRate(ageFromBirthYear(profile()?.birth_year));
    if (!hrMax) return null;
    const pct = bpm / hrMax;
    if (pct < 0.6) return { label: "Zone 1 · Recovery", color: "var(--text-3)" };
    if (pct < 0.7) return { label: "Zone 2 · Easy", color: "var(--success)" };
    if (pct < 0.8) return { label: "Zone 3 · Aerobic", color: "var(--accent)" };
    if (pct < 0.9) return { label: "Zone 4 · Threshold", color: "var(--warn)" };
    return { label: "Zone 5 · Max", color: "var(--danger)" };
  }

  // The card exists only in the native shell during a cardio session; rendered with lastBpm so a
  // body re-render (add/move/remove exercise) doesn't blank a reading that's already arrived.
  function liveHrCard() {
    if (!health.available() || !wantsLiveHr()) return null;
    const card = h("div", { class: "card", id: "live-hr", style: { marginBottom: "12px" } });
    setChildren(card, ...liveHrContent(lastBpm));
    return card;
  }

  function liveHrContent(bpm) {
    if (bpm == null) {
      return [h("div", { class: "hstack", style: { gap: "8px", alignItems: "center", color: "var(--text-3)" } },
        icon("pulse", { size: 18 }), h("span", { style: { fontSize: ".9rem" } }, "Waiting for heart rate…"))];
    }
    const zone = hrZone(bpm);
    const accent = zone?.color || "var(--accent)";
    return [
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
        h("div", { class: "hstack", style: { gap: "8px", alignItems: "center", color: accent } },
          icon("pulse", { size: 22 }),
          h("span", { style: { fontSize: "1.7rem", fontWeight: 700, lineHeight: 1 } }, String(bpm)),
          h("span", { style: { fontSize: ".8rem", fontWeight: 600, color: "var(--text-3)" } }, "bpm")),
        h("div", { class: "grow" }),
        zone ? h("span", { class: "ss-badge", style: { background: accent, color: "#fff" } }, zone.label) : null
      ),
    ];
  }

  // Free-text comment for the whole session (saved on blur).
  function sessionNotesCard() {
    return h("div", { class: "card mt-lg" },
      h("label", { class: "dim", style: { fontSize: ".8rem", fontWeight: 600 } }, "Workout notes"),
      h("textarea", {
        class: "textarea mt", rows: 2,
        placeholder: "How did it go? Anything you changed…",
        onchange: (e) => saveSessionNotes(e.target.value),
      }, session.notes || "")
    );
  }

  function saveSessionNotes(value) {
    const notes = value.trim() || null;
    if (notes === (session.notes || null)) return;
    session.notes = notes;
    touch();
    api.updateSession(session.id, { notes });
  }

  function rebuildExercise(ex) {
    const card = root.querySelector(`[data-se="${ex.id}"]`);
    if (card) card.replaceWith(exerciseCard(ex));
  }

  // All working sets logged (unchecked warm-ups don't hold an exercise open).
  function workingDone(ex) {
    const ws = ex.sets.filter((s) => !s.is_warmup);
    return ws.length > 0 && ws.every((s) => s.is_completed);
  }

  // Focus mode collapses a finished exercise to a summary row — but never the only
  // exercise of the session (there's nothing to focus on instead).
  function isCollapsed(ex) {
    return focusMode() && session.exercises.length > 1 && workingDone(ex) && !expandedOverride.has(ex.id);
  }

  // The next exercise that still has working sets left, starting after `after` and wrapping
  // around. One definition of "what's next" — the focus-mode scroll below and the always-on rest
  // face both go through it, so they can never disagree about where the workout is.
  function nextPendingExercise(after) {
    const idx = after ? session.exercises.findIndex((e) => e.id === after.id) : -1;
    const order = idx < 0
      ? session.exercises
      : [...session.exercises.slice(idx + 1), ...session.exercises.slice(0, idx)];
    return order.find((e) => !workingDone(e)) || null;
  }

  // …and the specific set inside it: the first unfinished working set of the exercise the user is
  // on, or of the next one once that exercise wraps. Warm-ups are skipped — they don't rest.
  function nextPendingSet(after) {
    const ex = after && !workingDone(after) ? after : nextPendingExercise(after);
    if (!ex) return null;
    const working = ex.sets.filter((s) => !s.is_warmup);
    const index = working.findIndex((s) => !s.is_completed);
    if (index < 0) return null;
    return { ex, set: working[index], index: index + 1, total: working.length };
  }

  // After an exercise wraps in focus mode, bring the next unfinished one into view so the
  // user lands on their next pending set without scrolling past collapsed cards.
  function scrollToNextPending(after) {
    if (!isCollapsed(after)) return;
    const next = nextPendingExercise(after);
    if (!next) return; // everything done — Finish sits in the sticky header already
    setTimeout(() => {
      root.querySelector(`[data-se="${next.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  }

  // ── Exercise card ───────────────────────────────────────────────────────────
  function exerciseCard(ex) {
    if (isCollapsed(ex)) return collapsedCard(ex);
    return expandedCard(ex);
  }

  // Summary row for a finished exercise: name + sets done, tap to re-expand. Same
  // data-se hook as the full card so rebuildExercise() swaps either form in place.
  function collapsedCard(ex) {
    const ss = supersetLabels(session.exercises)[session.exercises.findIndex((e) => e.id === ex.id)];
    const done = ex.sets.filter((s) => !s.is_warmup && s.is_completed).length;
    return h(
      "div",
      {
        class: ["ex-card", "ex-collapsed", "card-tap", ss && "ss-member"], dataset: { se: ex.id },
        title: "Show sets",
        onClick: () => { expandedOverride.add(ex.id); rebuildExercise(ex); },
      },
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
        ss ? h("span", { class: "ss-badge", title: "Superset" }, ss.label) : null,
        exerciseThumb(ex.images, { size: 32 }),
        h("span", { class: "ex-name grow", style: { fontSize: ".92rem" } }, ex.exercise_name),
        h("span", { class: "ecl-done" }, icon("check", { size: 15 }), `${done} set${done === 1 ? "" : "s"}`),
        icon("chevronDown", { size: 16, className: "icon muted" })
      )
    );
  }

  function expandedCard(ex) {
    // Superset label is derived from the live exercise order, so it's correct whether the
    // card is rendered in the full list or rebuilt on its own.
    const ss = supersetLabels(session.exercises)[session.exercises.findIndex((e) => e.id === ex.id)];
    const card = h("div", { class: ["ex-card", ss && "ss-member"], dataset: { se: ex.id } });

    // A finished exercise held open (focus-mode override) offers a collapse chevron.
    const collapseBtn = focusMode() && session.exercises.length > 1 && workingDone(ex)
      ? h("button", {
          class: "icon-btn ghost", title: "Collapse", "aria-label": "Collapse exercise",
          onClick: () => { expandedOverride.delete(ex.id); rebuildExercise(ex); },
        }, (() => { const c = icon("chevronDown", { size: 18 }); c.style.transform = "rotate(180deg)"; return c; })())
      : null;
    const isWeightReps = metricOf(ex) === "weight_reps";
    const plateBtn = isWeightReps
      ? h("button", {
          class: "icon-btn ghost",
          title: "Plate calculator",
          "aria-label": `Plate calculator for ${ex.exercise_name}`,
          onClick: () => {
            const firstWeight = ex.sets.find((s) => s.weight_kg != null && !s.is_warmup)?.weight_kg ?? ex.sets[0]?.weight_kg;
            openPlateCalculator({ weightKg: firstWeight });
          },
        }, icon("dumbbell", { size: 17 }))
      : null;

    const head = h(
      "div",
      { class: "ex-card-head" },
      ss ? h("span", { class: "ss-badge", title: "Superset" }, ss.label) : null,
      exerciseThumb(ex.images, { size: 40 }),
      h("div", { class: "grow session-ex-title" },
        h("div", { class: "session-ex-meta" },
          `EXERCISE ${String(session.exercises.indexOf(ex) + 1).padStart(2, "0")} / ${String(session.exercises.length).padStart(2, "0")}`,
          ex.rest_seconds
            ? ` · ${Math.round(ex.rest_seconds / 60)}m rest`
            : (ex.learned_rest_seconds ? ` · ~${Math.round(ex.learned_rest_seconds / 60)}m rest (auto)` : "")
        ),
        h("button", { class: "ex-name", onClick: () => navigate(`/exercise/${ex.library_exercise_id}`) }, ex.exercise_name),
        (ex.target_reps_min && ex.target_reps_max)
          ? h("span", { class: "dim", style: { fontSize: ".78rem", marginLeft: "6px" } }, `(${ex.target_reps_min}–${ex.target_reps_max} reps)`)
          : null
      ),
      collapseBtn,
      plateBtn,
      h("button", { class: "icon-btn ghost", "aria-label": `Options for ${ex.exercise_name}`, onClick: () => exerciseMenu(ex) }, icon("grip", { size: 18 }))
    );
    card.appendChild(head);
    if (!ex.sets.length || ex.sets.some((set) => !set.is_completed)) {
      card.appendChild(h("button", { class: "session-replace", "aria-label": `Replace ${ex.exercise_name}`,
        onClick: () => replaceExercise(ex) }, icon("flip", { size: 16 }), "Replace exercise", h("span", null, "Find an alternative")));
    }

    // On the first member of a superset, hint that sets should be alternated.
    if (ss && ss.index === 1) {
      card.appendChild(h("div", { class: "ss-hint" }, icon("link", { size: 13 }), `Superset — alternate sets across ${ss.size} exercises, rest after the last.`));
    }

    if (ex.video_url) {
      card.appendChild(
        h("a", { class: "ex-video", href: ex.video_url, target: "_blank", rel: "noopener noreferrer",
                 style: { display: "inline-flex", gap: "6px", alignItems: "center", margin: "2px 0 8px", fontSize: ".82rem", color: "var(--accent)", textDecoration: "none" } },
          icon("play", { size: 13 }), "Watch demo")
      );
    }

    if (ex.last_performance?.sets?.length) {
      const mt = metricOf(ex);
      const summary = ex.last_performance.sets.map((s) => setSummaryText(mt, s, { rpe: !rpeHidden() })).join(", ");
      card.appendChild(h("div", { class: "last" }, "Last time: ", h("b", null, summary)));
    }

    // Note left on this exercise in a prior session (e.g. "shoulder sore, dropped weight"), so a
    // flagged issue resurfaces. Hidden when it duplicates this session's own note (repeat-last copies it).
    if (ex.last_note && ex.last_note.note.trim() !== (ex.notes || "").trim()) {
      card.appendChild(
        h("div", { class: "last-note", style: { display: "flex", gap: "6px", alignItems: "flex-start", margin: "2px 0 8px", fontSize: ".82rem", color: "var(--warn)" } },
          icon("history", { size: 13 }),
          h("span", { style: { whiteSpace: "pre-wrap" } }, h("b", null, `Last time (${relativeDay(ex.last_note.date)}): `), ex.last_note.note))
      );
    }

    const sg = ex.last_performance?.suggestion;
    if (sg && metricOf(ex) === "weight_reps" && ex.sets.some(s=>!s.is_completed && !s.is_warmup)) {
      card.appendChild(
        h("div", {
          class: "coach", role: "button", tabindex: "0",
          title: "Apply this target to your remaining sets",
          style: { display: "flex", gap: "6px", alignItems: "center", margin: "2px 0 8px", fontSize: ".8rem", color: "var(--accent)", cursor: "pointer" },
          onClick: () => applySuggestion(ex, sg),
          onKeydown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); applySuggestion(ex, sg); } },
        },
          icon("trophy", { size: 13 }),
          h("span", { class: "grow" }, `${sg.reason} → `, h("b", null, `${toDisplayWeight(sg.weight_kg)}${weightLabel()} × ${sg.reps}`)),
          h("span", { class: "badge", style: { background: "var(--accent-soft)", color: "var(--accent)", fontWeight: 700, flex: "0 0 auto" } }, `Apply to ${ex.sets.filter(s=>!s.is_completed && !s.is_warmup).length} remaining sets`)
        )
      );
    }

    if(suggestionUndo.has(ex.id))card.append(h("div",{class:"suggestion-applied"},
      h("span",{role:"status"},"Suggested targets applied to remaining working sets."),
      h("button",{class:"btn btn-ghost btn-sm",onClick:()=>undoSuggestion(ex)},"Undo suggestion")));
    if (ex.notes) {
      card.appendChild(
        h("div", { class: "ex-note", style: { display: "flex", gap: "6px", alignItems: "flex-start", margin: "2px 0 8px", fontSize: ".82rem", color: "var(--muted)" }, onClick: () => editExerciseNote(ex) },
          icon("edit", { size: 13, className: "icon muted" }),
          h("span", { style: { whiteSpace: "pre-wrap" } }, ex.notes))
      );
    }

    const mt = metricOf(ex);
    const grid = h("div", { class: ["set-grid", `m-${mt}`, rpeHidden() && "no-rpe"] });
    grid.appendChild(
      h("div", { class: "set-row head" },
        h("div", null, "Set"),
        ...colsFor(mt).map((c) =>
          c === "rpe"
            ? h("div", { class: "rpe-head", title: "What is RPE?", onClick: openRpeInfo, style: { cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "2px" } },
                "RPE", icon("info", { size: 11, className: "icon muted" }))
            : c === "weight"
              ? h("div", {
                  class: "weight-head", title: "Plate calculator", style: { cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "3px" },
                  onClick: () => {
                    const firstWeight = ex.sets.find((s) => s.weight_kg != null && !s.is_warmup)?.weight_kg ?? ex.sets[0]?.weight_kg;
                    openPlateCalculator({ weightKg: firstWeight });
                  }
                }, COL_HEAD[c](), icon("dumbbell", { size: 11, className: "icon muted" }))
              : h("div", null, COL_HEAD[c]())),
        h("div", null, "Log"))
    );
    ex.sets.forEach((set, i) => grid.appendChild(setRow(ex, set, i)));
    card.appendChild(grid);

    card.appendChild(
      h(
        "div",
        { class: "set-add" },
        h("button", { class: "btn btn-ghost", onClick: () => addSet(ex, false) }, icon("plus", { size: 16 }), "Add set"),
        h("button", { class: "btn btn-ghost", onClick: () => addSet(ex, true) }, "Warm-up")
      )
    );
    return card;
  }

  function setRow(ex, set, index) {
    const mt = metricOf(ex);
    const warm = set.is_warmup;
    const row = h("div", { class: ["set-row", set.is_completed && "done"], dataset: { set: set.id } });

    const numBtn = h(
      "button",
      { class: ["set-num", warm && "warmup"], title: "Set options", onClick: () => setMenu(ex, set) },
      warm ? "W" : String(displayIndex(ex, set))
    );

    let stepperEl = null;
    function hideStepper() {
      if (stepperEl) {
        stepperEl.remove();
        stepperEl = null;
      }
    }

    function showStepper(col, inputEl) {
      hideStepper();
      let chips = [];
      if (col === "weight") {
        chips = [
          { label: "-5", delta: -5 },
          { label: "-2.5", delta: -2.5 },
          { label: "+2.5", delta: 2.5 },
          { label: "+5", delta: 5 },
        ];
      } else if (col === "reps") {
        chips = [
          { label: "-5", delta: -5 },
          { label: "-1", delta: -1 },
          { label: "+1", delta: 1 },
          { label: "+5", delta: 5 },
        ];
      } else if (col === "rpe") {
        chips = [
          { label: "7", val: 7 },
          { label: "8", val: 8 },
          { label: "8.5", val: 8.5 },
          { label: "9", val: 9 },
          { label: "9.5", val: 9.5 },
          { label: "10", val: 10 },
        ];
      }
      if (!chips.length) return;

      stepperEl = h(
        "div",
        { class: "set-stepper-strip" },
        ...chips.map((chip) =>
          h(
            "button",
            {
              type: "button",
              class: "stepper-chip",
              onpointerdown: (e) => {
                e.preventDefault();
                if (col === "weight") {
                  const current = parseFloat(inputEl.value) || 0;
                  const nextVal = Math.max(0, Math.round((current + chip.delta) * 100) / 100);
                  inputEl.value = nextVal;
                  patchSet(ex, set, { weight_kg: toStorageKg(nextVal) });
                } else if (col === "reps") {
                  const current = parseInt(inputEl.value, 10) || 0;
                  const nextVal = Math.max(0, current + chip.delta);
                  inputEl.value = nextVal;
                  patchSet(ex, set, { reps: nextVal });
                } else if (col === "rpe") {
                  inputEl.value = chip.val;
                  patchSet(ex, set, { rpe: chip.val });
                }
              },
            },
            chip.label
          )
        )
      );
      row.after(stepperEl);
    }

    row.addEventListener("focusout", () => {
      setTimeout(() => {
        if (!row.contains(document.activeElement)) {
          hideStepper();
        }
      }, 80);
    });

    // One input per metric column; each exposes read() returning its slice of the change
    // set, so the ✓ tap can capture every field at once (RPE/time can't rely on the input's
    // own change firing before the row rebuilds — same touch/DOM-churn class as the old bug).
    const fields = colsFor(mt).map((c) => makeField(ex, set, c));
    fields.forEach((field, i) => {
      field.el.setAttribute("aria-label", `${ex.exercise_name}, ${warm ? "warm-up" : "set"} ${index + 1}, ${COL_HEAD[colsFor(mt)[i]]()}`);
      field.el.setAttribute("enterkeyhint", "next");
      // Replacing a prefilled value should take one tap, without a long press or backspaces.
      field.el.addEventListener("focus", () => {
        field.el.select();
        showStepper(colsFor(mt)[i], field.el);
      });
      field.el.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" || event.isComposing) return;
        event.preventDefault();
        (fields[i + 1]?.el || check).focus();
      });
    });
    const read = () => Object.assign({}, ...fields.map((f) => f.read()));

    const check = h(
      "button",
      { class: "set-check", "aria-label": `${set.is_completed ? "Undo" : "Complete"} ${ex.exercise_name} set ${index + 1}`, "aria-pressed": String(!!set.is_completed), onClick: () => completeSet(ex, set, read) },
      icon("check", { size: 20 })
    );
    row.append(numBtn, ...fields.map((f) => f.el), check);
    return row;
  }

  function makeField(ex, set, col) {
    if (col === "weight") {
      const el = h("input", { class: "set-input", type: "number", inputmode: "decimal", step: "0.5", placeholder: "—",
        value: set.weight_kg != null ? toDisplayWeight(set.weight_kg) : "",
        onchange: (e) => patchSet(ex, set, { weight_kg: toStorageKg(e.target.value) }) });
      return { el, read: () => ({ weight_kg: toStorageKg(el.value) }) };
    }
    if (col === "reps") {
      const el = h("input", { class: "set-input", type: "number", inputmode: "numeric", placeholder: "—",
        value: set.reps ?? "",
        onchange: (e) => patchSet(ex, set, { reps: e.target.value === "" ? null : parseInt(e.target.value, 10) }) });
      return { el, read: () => ({ reps: el.value === "" ? null : parseInt(el.value, 10) }) };
    }
    if (col === "rpe") {
      const el = h("input", { class: "set-rpe", type: "number", inputmode: "decimal", step: "0.5", min: "1", max: "10", placeholder: "–",
        value: set.rpe ?? "",
        onchange: (e) => patchSet(ex, set, { rpe: e.target.value === "" ? null : parseFloat(e.target.value) }) });
      return { el, read: () => ({ rpe: el.value === "" ? null : parseFloat(el.value) }) };
    }
    if (col === "time") {
      const el = h("input", { class: "set-input", type: "text", inputmode: "numeric", placeholder: "m:ss",
        value: fmtTimeShort(set.duration_seconds),
        onchange: (e) => patchSet(ex, set, { duration_seconds: parseTimeInput(e.target.value) }) });
      return { el, read: () => ({ duration_seconds: parseTimeInput(el.value) }) };
    }
    // distance
    const el = h("input", { class: "set-input", type: "number", inputmode: "decimal", step: "0.01", placeholder: "—",
      value: set.distance_m != null ? toDisplayDistance(set.distance_m) : "",
      onchange: (e) => patchSet(ex, set, { distance_m: toStorageDistanceM(e.target.value) }) });
    return { el, read: () => ({ distance_m: toStorageDistanceM(el.value) }) };
  }

  function displayIndex(ex, set) {
    // Working-set number ignoring warm-ups.
    let n = 0;
    for (const s of ex.sets) {
      if (!s.is_warmup) n++;
      if (s.id === set.id) return n;
    }
    return n;
  }

  // ── Mutations (optimistic; persisted via the offline-resilient outbox) ────────
  function patchSet(ex, set, changes) {
    Object.assign(set, changes);
    touch();
    api.updateSet(set.id, changes);
  }

  // One tap turns the overload hint into the plan: every remaining (pending, working) set
  // gets the suggested weight × reps. Completed sets and warm-ups are never touched.
  function applySuggestion(ex, sg) {
    const pending = ex.sets.filter((s) => !s.is_completed && !s.is_warmup);
    if (!pending.length) {
      toast("All working sets are already done — add a set first.", "warn");
      return;
    }
    const changes = { weight_kg: sg.weight_kg, reps: sg.reps };
    suggestionUndo.set(ex.id,{changes,sets:pending.map(s=>({set:s,weight_kg:s.weight_kg,reps:s.reps}))});
    for (const s of pending) {
      Object.assign(s, changes);
      api.updateSet(s.id, changes);
    }
    touch();
    rebuildExercise(ex);
    toast(`Applied ${toDisplayWeight(sg.weight_kg)}${weightLabel()} × ${sg.reps} to ${pending.length} set${pending.length === 1 ? "" : "s"}`, "success");
  }

  function undoSuggestion(ex) {
    const undo=suggestionUndo.get(ex.id);if(!undo)return;
    let count=0;
    for(const old of undo.sets){
      const set=old.set;
      // A completed, removed, remotely changed, or manually edited set is no longer ours to undo.
      if(!ex.sets.includes(set) || set.is_completed || set.weight_kg!==undo.changes.weight_kg || set.reps!==undo.changes.reps)continue;
      const changes={weight_kg:old.weight_kg,reps:old.reps};Object.assign(set,changes);api.updateSet(set.id,changes);count++;
    }
    suggestionUndo.delete(ex.id);touch();rebuildExercise(ex);
    toast(count?`Restored ${count} pending set${count===1?"":"s"}`:"No unchanged pending targets to restore");
  }

  // Celebrate a record the moment it happens: a completed working set whose estimated 1RM
  // beats the pre-session best (server-provided). Quiet for exercises with no weighted
  // history — the finish summary still reports first-ever records. Local best advances so
  // only a FURTHER improvement toasts again this session.
  function maybeCelebratePr(ex, set) {
    if (metricOf(ex) !== "weight_reps" || set.is_warmup) return;
    if (!ex.best_e1rm_kg || set.weight_kg == null || !set.reps) return;
    const e = epley1RM(set.weight_kg, set.reps);
    if (e <= ex.best_e1rm_kg + 0.01) return;
    ex.best_e1rm_kg = Math.round(e * 10) / 10;
    if (navigator.vibrate) navigator.vibrate([30, 40, 60]);
    toast(`🏆 ${ex.exercise_name} record — est. 1RM ${fmtWeight(e)}`, "success");
  }

  function completeSet(ex, set, read) {
    const next = !set.is_completed;
    const changes = { is_completed: next };
    // Capture whatever is currently typed across this metric's fields. Reading here (not
    // relying on each input's own `change`) is what makes the ✓ tap commit values that the
    // touch-synthesised blur can drop mid-rebuild — the long-standing set-values-lost bug.
    if (next) Object.assign(changes, read());
    Object.assign(set, changes);
    // Finishing the last working set clears any manual re-expand, so the focus-mode
    // rebuild below renders the exercise collapsed again.
    if (next && workingDone(ex)) expandedOverride.delete(ex.id);
    rebuildExercise(ex);
    touch();
    api.updateSet(set.id, changes);
    if (next && !set.is_warmup) {
      if (navigator.vibrate) navigator.vibrate(35); // light haptic tick on ✓ (no-op where unsupported)
      maybeCelebratePr(ex, set);
      const rest = ex.rest_seconds || ex.learned_rest_seconds || profile()?.default_rest_seconds || 90;
      session.rest_ends_at = new Date(Date.now() + rest * 1000).toISOString();
      session.rest_source_set_id = set.id;
      startRest(rest, restOptions());
      scrollToNextPending(ex);
    }
  }

  function toggleWarmup(ex, set) {
    set.is_warmup = !set.is_warmup;
    rebuildExercise(ex);
    touch();
    api.updateSet(set.id, { is_warmup: set.is_warmup });
  }

  function setMenu(ex, set) {
    const m = openModal({
      title: `Set ${set.is_warmup ? "(warm-up)" : displayIndex(ex, set)}`,
      content: h(
        "div",
        null,
        metricOf(ex) === "weight_reps"
          ? h("button", { class: "btn btn-ghost btn-block", onClick: () => { m.close(); openPlateCalculator({ weightKg: set.weight_kg }); } }, icon("dumbbell", { size: 18 }), "Plate calculator")
          : null,
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); toggleWarmup(ex, set); } }, icon("flame", { size: 18 }), set.is_warmup ? "Make working set" : "Mark as warm-up"),
        h("button", { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" }, onClick: () => { m.close(); deleteSet(ex, set); } }, icon("trash", { size: 18 }), "Delete set")
      ),
    });
  }

  // RPE explainer — the column is unfamiliar to many lifters, so make the header tappable.
  function openRpeInfo() {
    const scale = [
      ["10", "Maximal — no reps left in the tank"],
      ["9", "1 rep left"],
      ["8", "2 reps left"],
      ["7", "3 reps left — solid working effort"],
      ["≤6", "Easy / warm-up effort"],
    ];
    openModal({
      title: "RPE — Rate of Perceived Exertion",
      content: h("div", null,
        h("p", { class: "dim", style: { fontSize: ".88rem", lineHeight: "1.5", marginTop: 0 } },
          "A 1–10 gauge of how hard a set felt — roughly how many reps you had left ", h("i", null, "in reserve"),
          ". It's optional, but logging it helps track effort and guide progression."),
        h("div", { class: "rpe-scale", style: { marginTop: "10px" } },
          ...scale.map(([n, desc]) => h("div", { class: "row-between", style: { padding: "7px 0", borderTop: "1px solid var(--border)" } },
            h("span", { class: "ss-badge", style: { background: "var(--rpe)", minWidth: "34px" } }, n),
            h("span", { class: "dim", style: { fontSize: ".85rem", textAlign: "right", marginLeft: "10px" } }, desc)
          ))
        )
      ),
    });
  }

  function deleteSet(ex, set) {
    ex.sets = ex.sets.filter((s) => s.id !== set.id);
    rebuildExercise(ex);
    touch();
    api.deleteSet(set.id);
  }

  async function addSet(ex, warmup) {
    // Works offline: the outbox queues the create under a client temp id and reconciles it
    // on reconnect. Prefill a working set from the previous set (mirrors the server's own
    // copy-last behaviour) so the optimistic row and the eventual server row agree.
    const prev = ex.sets[ex.sets.length - 1];
    const body = { is_warmup: !!warmup };
    if (!warmup && prev) {
      body.weight_kg = prev.weight_kg;
      body.reps = prev.reps;
      body.duration_seconds = prev.duration_seconds;
      body.distance_m = prev.distance_m;
    }
    try {
      const newSet = await api.addSet(ex.id, body);
      ex.sets.push(newSet);
      rebuildExercise(ex);
      touch();
    } catch (e) {
      toast(e?.status === 0 ? "You're offline — reconnect to add sets." : "Couldn't add set", "error");
    }
  }

  function replaceExercise(ex) {
    replacementModal = openExerciseReplacement({
      exercise: ex,
      existing: session.exercises.filter((item) => item.id !== ex.id).map((item) => item.library_exercise_id),
      onClose: () => { replacementModal = null; },
      onReplace: async (alternative, scope) => {
        const updated = await api.replaceSessionExercise(ex.id, {
          library_exercise_id: alternative.id,
          expected_exercise_id: ex.library_exercise_id,
          expected_template_exercise_id: ex.template_library_exercise_id,
          scope,
        });
        if (destroyed) return;
        session = updated;
        suggestionUndo.delete(ex.id);
        touch();
        renderBody(root.querySelector("#session-body"));
        syncSharedRest();
        toast(scope === "workout" ? "Exercise replaced. Saved workout updated too." : "Exercise replaced for this session.", "success");
        requestAnimationFrame(() => {
          const card = root.querySelector(`[data-se="${updated.replacement_exercise_id}"]`);
          card?.scrollIntoView({ behavior: "smooth", block: "center" });
          card?.querySelector(".session-ex-title .ex-name")?.focus({ preventScroll: true });
        });
      },
    });
  }

  function exerciseMenu(ex) {
    const m = openModal({
      title: ex.exercise_name,
      content: h(
        "div",
        null,
        (!ex.sets.length || ex.sets.some((set) => !set.is_completed)) && h("button", { class: "btn btn-ghost btn-block", onClick: () => { m.close(); replaceExercise(ex); } }, icon("flip", { size: 18 }), "Replace exercise"),
        h("button", { class: "btn btn-ghost btn-block", onClick: () => { m.close(); navigate(`/exercise/${ex.library_exercise_id}`); } }, icon("info", { size: 18 }), "View exercise"),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); editExerciseNote(ex); } }, icon("edit", { size: 18 }), ex.notes ? "Edit note" : "Add note"),
        h("div", { class: "btn-row mt" },
          h("button", { class: "btn btn-ghost grow", onClick: () => { m.close(); moveExercise(ex, -1); } }, icon("back", { size: 18 }), "Move up"),
          h("button", { class: "btn btn-ghost grow", onClick: () => { m.close(); moveExercise(ex, 1); } }, "Move down", icon("forward", { size: 18 }))
        ),
        h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); removeLastSet(ex); } }, icon("x", { size: 18 }), "Remove last set"),
        h("button", { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" }, onClick: async () => { m.close(); await removeExercise(ex); } }, icon("trash", { size: 18 }), "Remove exercise")
      ),
    });
  }

  function editExerciseNote(ex) {
    let note = ex.notes || "";
    const textarea = h("textarea", { class: "textarea", rows: 3, placeholder: "e.g. Substituted with machine — left shoulder sore", oninput: (e) => (note = e.target.value) }, note);
    const m = openModal({
      title: ex.exercise_name,
      content: h("div", null,
        h("label", { class: "dim", style: { fontSize: ".8rem" } }, "Note for this exercise"),
        h("div", { class: "mt" }, textarea),
        h("div", { class: "btn-row mt" },
          ex.notes ? h("button", { class: "btn btn-ghost", onClick: () => { m.close(); saveExerciseNote(ex, ""); } }, "Remove") : null,
          h("button", { class: "btn btn-primary grow", onClick: () => { m.close(); saveExerciseNote(ex, note); } }, "Save")
        )
      ),
    });
  }

  function saveExerciseNote(ex, value) {
    const notes = value.trim() || null;
    ex.notes = notes;
    rebuildExercise(ex);
    touch();
    api.updateSessionExercise(ex.id, { notes });
  }

  async function moveExercise(ex, dir) {
    const idx = session.exercises.findIndex((e) => e.id === ex.id);
    const target = idx + dir;
    if (target < 0 || target >= session.exercises.length) return;
    const arr = session.exercises;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    renderBody(root.querySelector("#session-body"));
    touch();
    api.reorderSessionExercises(session.id, arr.map((e) => e.id));
  }

  function removeLastSet(ex) {
    const last = ex.sets[ex.sets.length - 1];
    if (!last) return;
    ex.sets.pop();
    rebuildExercise(ex);
    touch();
    api.deleteSet(last.id);
  }

  function removeExercise(ex) {
    session.exercises = session.exercises.filter((e) => e.id !== ex.id);
    touch();
    renderBody(root.querySelector("#session-body"));
    api.removeSessionExercise(ex.id);
  }

  function addExercise() {
    // Works offline: the outbox queues the create under client temp ids; the optimistic
    // exercise (built from the picked library row) renders immediately and reconciles on
    // reconnect. We append the one new exercise rather than swapping the whole session, so
    // any other unsynced local edits are preserved.
    openExercisePicker({
      title: "Add exercise",
      multi: false,
      onPick: async (lib) => {
        try {
          const ex = await api.addSessionExercise(session.id, lib);
          session.exercises.push(ex);
          touch();
          renderBody(root.querySelector("#session-body"));
          ensureLiveHr(); // a freshly-added cardio exercise should start the HR readout
        } catch (e) {
          toast(e?.status === 0 ? "You're offline — reconnect to add exercises." : "Couldn't add exercise", "error");
        }
      },
    });
  }

  // ── Finish / discard ──────────────────────────────────────────────────────────
  async function finish() {
    const completed = session.exercises.reduce((n, ex) => n + ex.sets.filter((s) => s.is_completed).length, 0);
    if (completed === 0) {
      const ok = await confirmDialog({ title: "Finish empty workout?", message: "You haven't completed any sets. Discard instead?", confirmText: "Discard", danger: true });
      if (ok) return discard();
      return;
    }
    const ok = await confirmDialog({ title: "Finish workout?", message: "Save this session to your history.", confirmText: "Finish" });
    if (!ok) return;

    // Drain queued edits first so the server has every set before it computes the summary.
    await outbox.flush();

    const elapsed = Math.floor((Date.now() - (parseDate(session.started_at)?.getTime() ?? Date.now())) / 1000);
    let res;
    try {
      res = await api.completeSession(session.id, { duration_seconds: elapsed });
    } catch (e) {
      toast(e.message || "Couldn't finish", "error");
      return;
    }

    // Either way the workout is over on this device: stop the rest, drop the active session.
    cacheActiveSession(null);
    stopRest();
    destroy();
    activeSession(null);

    if (res?.queued) {
      // NOT outbox.clear() — the queued sets and the completion behind them are the only copy
      // of this workout the server hasn't seen. Clearing here would throw the session away.
      markFinishing(session.name || "Workout");
      showPendingFinish(elapsed);
      return;
    }
    outbox.clear();
    showSummary(res.summary);
  }

  // The offline finish. Deliberately not the celebration: the workout is saved on this device
  // and has not reached the server, and dressing that up as "Workout complete!" would be a claim
  // we can't stand behind. Records especially are computed server-side against the whole
  // history, so we show what's actually known locally and promise nothing else yet.
  function showPendingFinish(elapsed) {
    let volume = 0, sets = 0;
    for (const ex of session.exercises) {
      for (const st of ex.sets) {
        if (!st.is_completed || st.is_warmup) continue;
        sets++;
        volume += (st.weight_kg || 0) * (st.reps || 0);
      }
    }
    const body = h(
      "div",
      { class: "center" },
      h("div", { style: { color: "var(--warn)" } }, icon("history", { size: 40 })),
      h("h3", { class: "mt" }, "Saved on this device"),
      h("p", { class: "dim", style: { fontSize: ".88rem", lineHeight: 1.5, margin: "6px 0 0" } },
        "Your workout is finished and every set is stored here. Kashtu will send it the moment you're back online — you don't need to keep the app open."),
      h("div", { class: "stat-grid mt-lg", style: { textAlign: "left" } },
        stat(fmtDuration(elapsed), "Duration"),
        stat(String(sets), "Sets"),
        stat(fmtVolume(volume).split(" ")[0], "Volume (" + weightLabel() + ")")),
      h("p", { class: "dim", style: { fontSize: ".8rem", marginTop: "12px" } },
        "Records and achievements are worked out once it syncs."),
      h("button", { class: "btn btn-primary btn-block btn-lg mt-lg", onClick: () => { m.close(); navigate("/"); } }, "Done")
    );
    const m = openModal({ title: "", content: body, onClose: () => navigate("/") });
  }

  function showSummary(summary) {
    const body = h(
      "div",
      { class: "center" },
      // The bow finally resolves at the moment that earned it — the workout, not the week.
      bowLoose({ size: 84 }),
      h("h3", { class: "mt" }, "Workout complete!"),
      h("p",{class:"completion-fact",role:"status"},`${summary.working_sets ?? summary.total_sets} working sets logged across ${summary.exercise_count ?? session.exercises.length} exercises`),
      summary.planned_sets?h("p",{class:"dim"},`${summary.planned_completed} of ${summary.planned_sets} originally planned working sets completed.`):null,
      h("div",{class:"review-facts"},stat(fmtDuration(summary.duration_seconds),"Session duration"),
        summary.distance_m>0?stat(`${(summary.distance_m/1000).toLocaleString()} km`,"Distance logged"):null),
      summary.comparisons?.length?h("div",{class:"completion-comparison"},h("strong",null,comparisonText(summary.comparisons[0])),h("p",{class:"dim"},`Compared with ${summary.comparisons[0].baseline_sessions} earlier sessions in the preceding four weeks. One result isn't a long-term trend.`)):null,
      h("details",{class:"completion-details"},h("summary",null,"All session totals"),h("div",{class:"review-facts"},stat(String(summary.total_sets),"All sets, including warm-ups"),summary.total_volume_kg>0?stat(fmtVolume(summary.total_volume_kg),"Volume logged"):null)),
      (summary.prs.length > 0
        ? h(
          "div",
          { class: "mt-lg", style: { textAlign: "left" } },
          h("div", { class: "section-title", style: { margin: "0 0 8px" } }, "Personal records"),
          ...summary.prs.map((pr) =>
            h("div", { class: "row-between", style: { padding: "6px 0" } }, h("span", null, pr.exercise_name), h("span", { class: "badge badge-pr" }, icon("trophy", { size: 14, fill: true }), `${fmtWeight(pr.estimated_1rm_kg)} 1RM`))
          )
        )
        : null),
      summary.bodyweight_logged_today || !(profile()?.home_tools || ["weight"]).includes("weight") ? null : h("details",{class:"completion-details"},h("summary",null,"Log bodyweight (optional)"),bodyweightPrompt()),
      h("button",{class:"btn btn-ghost btn-block mt",onClick:()=>editNextSession(()=>{},session.workout_id)},"Plan my next session"),
      h("button", { class: "btn btn-primary btn-block btn-lg mt-lg", onClick: () => { m.close(); navigate("/history"); } }, "View history"),
      h("button", { class: "btn btn-ghost btn-block mt", onClick: () => { m.close(); navigate("/"); } }, "Done")
    );
    const m = openModal({ title: "", content: body, onClose: () => navigate("/") });
  }

  function stat(num, label) {
    return h("div", { class: "stat" }, h("div", { class: "num accent" }, num), h("div", { class: "lbl" }, label));
  }

  // Skippable "log today's bodyweight" card shown on the finish summary.
  function bodyweightPrompt() {
    const wrap = h("div", { class: "card mt-lg", style: { textAlign: "left" } });
    const cur = profile()?.bodyweight_kg;
    const input = h("input", { class: "input grow", type: "number", inputmode: "decimal", step: weightLabel() === "kg" ? "0.05" : "1", value: cur != null ? toDisplayWeight(cur) : "", placeholder: `Weight in ${weightLabel()}` });
    const morning = h("input", { type: "checkbox", checked: isInMorningWeightWindow(profile()?.wake_time) });
    async function save() {
      const kg = toStorageKg(input.value);
      if (kg == null || kg <= 0) { toast("Enter a valid weight", "warn"); return; }
      try {
        await api.logBodyweight({ weight_kg: kg, source: "session", is_first_morning: morning.checked });
        setChildren(wrap, h("div", { class: "hstack", style: { gap: "8px", color: "var(--success)" } }, icon("check", { size: 18 }), h("span", null, `Logged ${toDisplayWeight(kg)} ${weightLabel()}`)));
      } catch (e) { toast(e.message, "error"); }
    }
    setChildren(wrap,
      h("div", { style: { fontWeight: 600 } }, "Log today's bodyweight"),
      h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px", marginBottom: "10px" } }, "Optional — track your weight over time."),
      h("div", { class: "hstack", style: { gap: "8px" } }, input, h("button", { class: "btn btn-primary", onClick: save }, "Save")),
      h("label", { class: "hstack", style: { gap: "8px", alignItems: "center", marginTop: "10px", cursor: "pointer", fontSize: ".82rem" } },
        morning, "First-morning, post-void")
    );
    return wrap;
  }

  async function discard() {
    const ok = await confirmDialog({ title: "Discard workout?", message: "This active session will be deleted.", confirmText: "Discard", danger: true });
    if (!ok) return;
    try {
      await api.deleteSession(session.id);
      outbox.clear();
      cacheActiveSession(null);
      stopRest();
      destroy();
      activeSession(null);
      navigate("/", { replace: true });
    } catch (e) {
      toast(e?.status === 0 ? "You're offline — reconnect to discard." : "Couldn't discard", "error");
    }
  }

  // Tear down the timer, outbox subscription and rest timer when the router detaches us.
  onUnmount(root, destroy);

  return root;
}
